import React, { useState, useCallback } from 'react';
import { useRouter } from 'hooks/use-router';
import { Layout } from './user-detail.styled';
import { Root } from '../../index.styled';
import useAppBarTitle from 'hooks/use-app-bar-title';
import DropdownButton from 'components/control/dropdown-button/dropdown-button';
import useUsersDetail from '../../hooks/use-users-detail';
import {
    EManageAccountAction,
    EUserStatus, IEKYCDetails,
    IEKYCUserFormat,
    IGetEKYCDetailsResponse,
} from 'types/entities/users';
import DialogPinReset from '../../dialogs/pin-reset';
import { toast } from 'react-toastify';
import messages from 'messages';
import DialogUnlock from '../../dialogs/unlock';
import DialogLock from '../../dialogs/lock';
import DialogDel from '../../dialogs/del';
import { Grid } from '@material-ui/core';
import DialogSuspend from '../../dialogs/suspend';
import DialogActivate from '../../dialogs/activate';
import UserAccountInfo from '../../components/user-account';
import useCurrentSessionData from 'hooks/use-current-session-data';
import PageHeader from 'components/layout/page-header/page-header';
import {
    routes,
    ERoutesPrivate,
    ERoutesType,
} from '../../../../../../types/routes';
import { EButtonSize } from '../../../ekyc/components/control/button/button.types';
import Button from '../../../ekyc/components/control/button/button';
import { users } from '../../../../../../types';
import useUsersEkycDetailGet from '../../hooks/use-user-ekyc-get-new';
import { EDocumentTypes } from '../../../../../../types/entities/documents';
import { EPendingTaskTypeName } from '../../../../../../types/entities/pending-tasks';


type TUserDetail = {};
type TUserDetailQuery = {
    id: string;
};

const pageTitle = 'User Details';

const UserDetail: React.FC<TUserDetail> = () => {
    useAppBarTitle(pageTitle);
    const { permissions } = useCurrentSessionData();
    const { history, query } = useRouter<TUserDetailQuery>();
    const pathName = history.location.pathname;
    const userId = pathName.split('/')[2];

    const [dialogResetPinOpen, setDialogResetPinOpen] =
        useState<boolean>(false);
    const [dialogDel, setDialogDel] = useState<boolean>(false);
    const [dialogUnlockOpen, setDialogUnlockOpen] = useState<boolean>(false);
    const [isDialogLockOpen, setDialogLockOpen] = useState<boolean>(false);
    const [isDialogSuspendOpen, setDialogSuspendOpen] =
        useState<boolean>(false);
    const [isDialogActivateOpen, setDialogActivateOpen] =
        useState<boolean>(false);
    const [curUserData, setCurUserData] = useState<users.TUsersDetail | null>();
    const [ekycData, setEkycData] = useState<IGetEKYCDetailsResponse | null>();
    const [ekycDataFormat, setEkycDataFormat] =
        useState<IEKYCUserFormat | null>();

    const { isLoading, refetch, isError, error } = useUsersDetail(query.id, {
        onSuccess: (data) => {
            setCurUserData(data as users.TUsersDetail);
        },
    });

    useUsersEkycDetailGet(userId, {
        onSuccess: (data) => {
            const parsedData = data as IGetEKYCDetailsResponse;
            setEkycData(parsedData);
            const ekycData = {
                id: parsedData.id,
                ekycDetails: parsedData.ekycDetails,
                ekycDocuments: [
                    {
                        type: EDocumentTypes.KTP,
                        imageData: {
                            content: parsedData.ktp,
                            name: 'ktpphoto.jpg',
                            type: 'image/jpeg',
                        },
                    },
                    {
                        type: EDocumentTypes.Selfie,
                        imageData: {
                            content: parsedData.selfie,
                            name: 'selfie.jpg',
                            type: 'image/jpeg',
                        },
                    },
                ],
                validations: parsedData.validations,
            } as IEKYCUserFormat;
            setEkycDataFormat(ekycData);
        },
    });

    const handleEKYCDetails = () => {
        history.push(
            `${routes[ERoutesType.Private][ERoutesPrivate.EKYC][0]}/${userId}`,
            { ekycUserFormat: ekycDataFormat, ekycData: ekycData },
        );
    };

    const handleOnSuccessMutation = useCallback(() => {
        refetch();
    }, [refetch]);

    const manageAccountActionList = [
        {
            name: EManageAccountAction.RESET_PIN,
            isShow:
                curUserData?.status !== EUserStatus.CLOSED &&
                permissions.userPinEdit,
            callback: () => {
                setDialogResetPinOpen(true);
            },
        },
        {
            name: EManageAccountAction.LOCK_ACCOUNT,
            isShow:
                curUserData?.loginBlockedAt === null && permissions.userPinEdit,
            callback: () => {
                setDialogLockOpen(true);
            },
        },
        {
            name: EManageAccountAction.UNLOCK_ACCOUNT,
            isShow:
                curUserData?.loginBlockedAt !== null && permissions.userPinEdit,
            callback: () => {
                setDialogUnlockOpen(true);
            },
        },
        {
            name: EManageAccountAction.CLOSE_ACCOUNT,
            isShow:
                curUserData?.status !== EUserStatus.CLOSED &&
                permissions.userAccountEdit,
            callback: () => {
                setDialogDel(true);
            },
        },
        {
            name: EManageAccountAction.SUSPEND_ACCOUNT,
            isShow:
                curUserData?.status !== EUserStatus.SUSPENDED &&
                permissions.userSuspendEdit,
            callback: () => {
                setDialogSuspendOpen(true);
            },
        },
        {
            name: EManageAccountAction.ACTIVATE_ACCOUNT,
            isShow:
                (curUserData?.status === EUserStatus.SUSPENDED || curUserData?.status === EUserStatus.DORMANT) &&
                permissions.userSuspendEdit,
            callback: () => {
                setDialogActivateOpen(true);
            },
        },
    ];

    return (
        <>
            <Root>
                <Layout>
                    {isLoading ? (
                        <>Loading</>
                    ) : isError ? (
                        // TODO: add style for errors
                        <>{error}</>
                    ) : (
                        curUserData && (
                            <>
                                <PageHeader
                                    leftElement={
                                        <DropdownButton
                                            label={'Manage Account'}
                                            actionList={manageAccountActionList}
                                            disabled={!curUserData}
                                        />
                                    }
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <UserAccountInfo user={curUserData} ekycDetails={ekycData?.ekycDetails as IEKYCDetails} />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Grid item xs={12}>
                                            <Button
                                                size={EButtonSize.Normal}
                                                onClick={handleEKYCDetails}
                                            >
                                                EKYC Details
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    {/* <Grid item xs={12}>
                                        Add Other section here if needed
                                    </Grid> */}
                                </Grid>
                            </>
                        )
                    )}
                </Layout>
            </Root>
            {/* TODO: refactor this when features go stable since many duplicated behavior */}
            {curUserData && (
                <>
                    <DialogPinReset
                        data={curUserData}
                        isDialogOpen={dialogResetPinOpen}
                        setIsDialogOpen={setDialogResetPinOpen}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success(
                                    messages.pendingTasks.onCreatePendingTask.onSuccess(EPendingTaskTypeName.RESET_PIN),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                            onError: (e) => {
                                toast.error(
                                    messages.users.onPinReset?.onError &&
                                        messages.users.onPinReset?.onError(e),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                        }}
                    />
                    <DialogUnlock
                        data={curUserData}
                        isDialogOpen={dialogUnlockOpen}
                        setIsDialogOpen={setDialogUnlockOpen}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success(
                                    messages.pendingTasks.onCreatePendingTask.onSuccess(EPendingTaskTypeName.UNBLOCK_USER),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                            onError: (e) => {
                                toast.error(
                                    messages.users.onUnblock?.onError &&
                                        messages.users.onUnblock?.onError(e),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                        }}
                    />
                    <DialogLock
                        data={curUserData}
                        isDialogOpen={isDialogLockOpen}
                        setIsDialogOpen={setDialogLockOpen}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success('User is locked!');
                            },
                            onError: (e) => {
                                toast.error(`Error: ${e}`, {
                                    pauseOnHover: true,
                                });
                            },
                        }}
                    />
                    <DialogDel
                        data={curUserData}
                        isDialogOpen={dialogDel}
                        setIsDialogOpen={setDialogDel}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success(
                                    messages.pendingTasks.onCreatePendingTask.onSuccess(EPendingTaskTypeName.CLOSE_ACCOUNT),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                            onError: (e) => {
                                toast.error(`Error: ${e}`, {
                                    pauseOnHover: true,
                                });
                            },
                        }}
                    />
                    <DialogSuspend
                        data={curUserData}
                        isDialogOpen={isDialogSuspendOpen}
                        setIsDialogOpen={setDialogSuspendOpen}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success('User is suspended!');
                            },
                            onError: (e) => {
                                toast.error(`Error: ${e}`, {
                                    pauseOnHover: true,
                                });
                            },
                        }}
                    />
                    <DialogActivate
                        data={curUserData}
                        isDialogOpen={isDialogActivateOpen}
                        setIsDialogOpen={setDialogActivateOpen}
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                handleOnSuccessMutation();
                                toast.success(
                                    messages.pendingTasks.onCreatePendingTask.onSuccess(EPendingTaskTypeName.ACTIVATE_USER),
                                    {
                                        pauseOnHover: true,
                                    },
                                );
                            },
                            onError: (e) => {
                                toast.error(`Error: ${e}`, {
                                    pauseOnHover: true,
                                });
                            },
                        }}
                    />
                </>
            )}
        </>
    );
};

export default UserDetail;
